import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Copy, Mail, RefreshCw, Trash2, Users, X } from 'lucide-react';
import {
  ROLE_LABELS,
  ROLES,
  roleAtLeast,
  SHARED_ROLES,
  type AccessEntry,
  type AccessSource,
  type BoardSharing,
  type BoardSnapshot,
  type Role,
  type SharingCandidate,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { queryClient, useBoardAction } from '../../lib/board';
import { toast } from '../../lib/toast';
import { GROUP_DOT } from '../../lib/groupColors';
import { Avatar, Button, cx, IconButton, Sheet } from '../ui/ui';
import { ShareLinksSection, useShareLinks } from './ShareLinks';
import { Headline, PersonName } from '../PersonDialog';

const field = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base sm:min-h-10 sm:text-sm';
const badge = 'mr-1 inline-block rounded px-1.5 py-px text-[11px] font-medium';
const BADGE = {
  direkt: cx(badge, 'bg-black/5 text-[var(--ink)] dark:bg-white/10'),
  gruppe: cx(badge, 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-200'),
  extern: cx(badge, 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200'),
  linkgast: cx(badge, 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'),
};

const sharingKey = (boardId: string) => ['sharing', boardId];
const accessKey = (boardId: string) => ['access', boardId];

/** Eine Quelle in Worten: „Bearbeiter über Gruppe Gestaltung“ */
export function sourceText(s: AccessSource): string {
  const r = ROLE_LABELS[s.role];
  if (s.source === 'group') return `${r} über Gruppe „${s.via?.name ?? ''}“`;
  if (s.source === 'link') return `${r} über Einladungslink „${s.via?.name ?? ''}“`;
  return `${r} direkt`;
}

/**
 * Freigaben eines Boards (Bericht „Teams, Gruppen, Berechtigungen“, U01–U04).
 * Besitzer: „Personen & Gruppen“ und „Einladungslinks“, Zugriff erklären, Besitz übergeben.
 * Alle anderen sehen, wer Zugriff hat.
 */
export function MembersDialog({ snapshot, open, onClose }: { snapshot: BoardSnapshot; open: boolean; onClose: () => void }) {
  const isOwner = snapshot.board.role === 'owner';
  const [tab, setTab] = useState<'personen' | 'links'>('personen');
  // Beim erneuten Öffnen wieder mit „Personen & Gruppen“ beginnen
  useEffect(() => {
    if (open) setTab('personen');
  }, [open]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={isOwner ? 'Freigaben' : 'Mitglieder'} side="center">
      {isOwner ? (
        <div className="flex flex-col gap-4 p-4">
          <div role="tablist" aria-label="Freigaben" className="flex rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
            {(
              [
                ['personen', 'Personen & Gruppen'],
                ['links', 'Einladungslinks'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                className={cx('min-h-11 flex-1 rounded-md px-3 text-sm sm:min-h-10', tab === k ? 'bg-[var(--panel)] font-semibold shadow-sm' : 'text-[var(--ink-soft)]')}
              >
                {label}
              </button>
            ))}
          </div>
          <div role="tabpanel" aria-label={tab === 'personen' ? 'Personen & Gruppen' : 'Einladungslinks'}>
            {tab === 'personen' ? <PeopleAndGroups snapshot={snapshot} open={open} /> : <LinksTab snapshot={snapshot} open={open} />}
          </div>
        </div>
      ) : (
        <ReadOnlyMembers snapshot={snapshot} />
      )}
    </Sheet>
  );
}

function LinksTab({ snapshot, open }: { snapshot: BoardSnapshot; open: boolean }) {
  const boardId = snapshot.board.id;
  const memberKey = snapshot.members.length * 31 + snapshot.members.filter((m) => m.viaLinkId).length;
  const { data: links } = useShareLinks(boardId, open, memberKey);
  return <ShareLinksSection boardId={boardId} links={links} />;
}

/* ---------- Personen & Gruppen ---------- */

function PeopleAndGroups({ snapshot, open }: { snapshot: BoardSnapshot; open: boolean }) {
  const boardId = snapshot.board.id;
  const action = useBoardAction(boardId);
  const { data: me } = useMe();
  const { data } = useQuery({ queryKey: sharingKey(boardId), queryFn: () => api.get<BoardSharing>(`/api/boards/${boardId}/sharing`), enabled: open });
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const [showAccess, setShowAccess] = useState(false);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: sharingKey(boardId) });
    void queryClient.invalidateQueries({ queryKey: accessKey(boardId) });
  };
  const run = (fn: () => Promise<unknown>) =>
    action.mutate(async () => {
      try {
        await fn();
      } finally {
        refresh();
      }
    });

  const inviteEmail = (mail: string, r: Role) =>
    run(async () => {
      const res = await api.post<{ status: 'added' | 'invited'; inviteUrl?: string }>(`/api/boards/${boardId}/members`, { email: mail, role: r });
      if (res.status === 'added') {
        toast.info(`${mail} hat jetzt Zugriff.`);
        setLink(null);
      } else if (res.inviteUrl) setLink({ email: mail, url: res.inviteUrl });
    });

  if (!data) return <p className="text-sm text-[var(--ink-soft)]">Lade …</p>;
  const team = !!data.teamId;
  const direct = data.people.filter((p) => p.direct);
  const headlineOf = (id: string) => snapshot.members.find((m) => m.id === id)?.headline;
  const linkOnly = data.people.filter((p) => !p.direct);

  return (
    <div className="flex flex-col gap-4">
      <AddForm
        data={data}
        onPick={(c, role) =>
          c.kind === 'group'
            ? run(async () => {
                await api.put(`/api/boards/${boardId}/groups/${c.id}`, { role });
                toast.info(`Gruppe „${c.name}“ hat jetzt Zugriff (${ROLE_LABELS[role]}).`);
              })
            : inviteEmail(c.detail, role)
        }
        onEmail={inviteEmail}
      />

      {link && (
        <div role="status" className="flex flex-col gap-2 rounded-xl border border-accent/40 bg-accent-soft/40 p-3 text-sm">
          <p>
            Einladung an <strong>{link.email}</strong> ist per E-Mail unterwegs (sofern E-Mail eingerichtet ist). Du kannst den Link auch selbst schicken – er
            gilt 14 Tage und nur einmal:
          </p>
          <div className="flex gap-2">
            <input readOnly value={link.url} aria-label="Einladungslink" onFocus={(e) => e.target.select()} className={cx(field, 'min-w-0 flex-1 text-xs')} />
            <Button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link.url);
                  toast.info('Link kopiert.');
                } catch {
                  toast.info('Kopieren nicht möglich – Link markieren und kopieren.');
                }
              }}
            >
              <Copy size={16} /> Kopieren
            </Button>
          </div>
        </div>
      )}

      <ul aria-label="Freigaben" className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
        {direct.map((p) => {
          const gainsMore = p.effective && p.direct && p.effective !== p.direct && roleAtLeast(p.effective, p.direct);
          const kind = p.guest ? 'linkgast' : p.external ? 'extern' : 'direkt';
          return (
            <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5">
              <Avatar user={p} size={32} />
              <div className="min-w-[65%] flex-1 sm:min-w-0">
                <p className="truncate text-sm font-medium">
                  <PersonName id={p.id} name={p.name} />
                  {p.id === me?.id && ' (du)'}
                </p>
                <Headline text={headlineOf(p.id)} />
                <p className="truncate text-xs text-[var(--ink-soft)]">
                  <span className={BADGE[kind]}>{kind === 'direkt' ? 'Direkt' : kind === 'extern' ? 'Extern' : 'Linkgast'}</span>
                  {p.email}
                  {p.links.length > 0 && ` · auch über Link „${p.links.map((l) => l.name).join('“, „')}“`}
                </p>
              </div>
              <select
                value={p.direct!}
                aria-label={`Rolle von ${p.name}`}
                aria-describedby={gainsMore ? `eff-${p.id}` : undefined}
                className={cx(field, 'ml-11 flex-1 sm:ml-0 sm:flex-none')}
                onChange={(e) => {
                  const next = e.target.value as Role;
                  run(async () => {
                    const r = await api.patch<{ effectiveRole: Role | null; sources: AccessSource[] }>(`/api/boards/${boardId}/members/${p.id}`, { role: next });
                    if (r.effectiveRole && r.effectiveRole !== next) {
                      const other = r.sources.filter((s) => s.source !== 'user').map(sourceText);
                      toast.info(`Persönlich jetzt ${ROLE_LABELS[next]}. Insgesamt gilt weiter ${ROLE_LABELS[r.effectiveRole]}${other.length ? ` (${other.join(', ')})` : ''}.`);
                    }
                  });
                }}
              >
                {ROLES.filter((r) => (!p.guest && !p.external) || r !== 'owner').map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <RemoveButton
                label={p.id === me?.id ? 'Board verlassen' : `Freigabe für ${p.name} entfernen`}
                onClick={() => {
                  const self = p.id === me?.id;
                  if (
                    !window.confirm(
                      self
                        ? 'Board wirklich verlassen? Deine persönliche Freigabe und Linkbeitritte enden. Zugriff über eine Gruppe bleibt bestehen.'
                        : `Persönliche Freigabe für ${p.name} entfernen?${p.links.length ? ' Der Beitritt über einen Einladungslink bleibt bestehen.' : ''}`,
                    )
                  )
                    return;
                  run(async () => {
                    const res = await api.del<{ remaining?: AccessSource[] }>(`/api/boards/${boardId}/members/${p.id}?quelle=${self ? 'alle' : 'direkt'}`);
                    const via = res?.remaining?.map(sourceText);
                    toast.info(via?.length ? `Persönliche Freigabe entfernt. ${p.name} hat weiterhin Zugriff: ${via.join(', ')}.` : `${p.name} hat keinen Zugriff mehr.`);
                  });
                }}
              />
              {gainsMore && (
                <p id={`eff-${p.id}`} className="w-full pl-11 text-xs text-[var(--ink-soft)]">
                  Insgesamt gilt {ROLE_LABELS[p.effective!]} – über eine weitere Freigabe. Diese Rolle hier ändert daran nichts.
                </p>
              )}
            </li>
          );
        })}
        {data.groups.map((g) => (
          <li key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5">
            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
              <Users size={16} />
            </span>
            <div className="min-w-[65%] flex-1 sm:min-w-0">
              <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                <span className={cx('size-2.5 shrink-0 rounded-full', GROUP_DOT[g.color])} aria-hidden />
                {g.name}
              </p>
              <p className="truncate text-xs text-[var(--ink-soft)]">
                <span className={BADGE.gruppe}>Gruppe</span>
                {g.memberCount === 1 ? '1 Mitglied' : `${g.memberCount} Mitglieder`}
              </p>
            </div>
            <select
              value={g.role}
              aria-label={`Rolle der Gruppe ${g.name}`}
              className={cx(field, 'ml-11 flex-1 sm:ml-0 sm:flex-none')}
              onChange={(e) => {
                const next = e.target.value as Role;
                run(() => api.put(`/api/boards/${boardId}/groups/${g.id}`, { role: next }));
              }}
            >
              {SHARED_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            <RemoveButton
              label={`Freigabe für Gruppe ${g.name} entfernen`}
              onClick={() => {
                if (!window.confirm(`Freigabe für die Gruppe „${g.name}“ entfernen?\n\nIhre Mitglieder verlieren den Zugriff, sofern sie ihn nicht auf anderem Weg haben.`)) return;
                run(async () => {
                  const r = await api.del<{ lost: number }>(`/api/boards/${boardId}/groups/${g.id}`);
                  toast.info(r.lost ? `${r.lost === 1 ? 'Eine Person hat' : `${r.lost} Personen haben`} dadurch keinen Zugriff mehr.` : 'Freigabe entfernt. Alle Mitglieder haben weiterhin Zugriff auf anderem Weg.');
                });
              }}
            />
          </li>
        ))}
        {linkOnly.map((p) => (
          <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
            <Avatar user={p} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                <PersonName id={p.id} name={p.name} />
              </p>
              <p className="truncate text-xs text-[var(--ink-soft)]">
                <span className={p.guest ? BADGE.linkgast : BADGE.extern}>{p.guest ? 'Linkgast' : p.external ? 'Extern' : 'Über Link'}</span>
                {p.effective ? ROLE_LABELS[p.effective] : 'abgelaufen'} über „{p.links.map((l) => l.name).join('“, „')}“
              </p>
            </div>
            <RemoveButton
              label={`Linkbeitritt von ${p.name} entfernen`}
              onClick={() => {
                if (!window.confirm(`Linkbeitritt von ${p.name} entfernen? Solange der Einladungslink gilt, ist ein erneuter Beitritt möglich – zum Sperren den Link löschen.`)) return;
                run(() => api.del(`/api/boards/${boardId}/members/${p.id}?quelle=link`));
              }}
            />
          </li>
        ))}
        {snapshot.invitations.map((i) => (
          <li key={i.email} className="flex items-center gap-3 px-3 py-2.5">
            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
              <Mail size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{i.email}</p>
              <p className="text-xs text-[var(--ink-soft)]">
                Eingeladen als {ROLE_LABELS[i.role]} – {!i.expiresAt || new Date(i.expiresAt) < new Date() ? 'Link abgelaufen' : 'noch nicht angenommen'}
              </p>
            </div>
            <IconButton label={`Neuen Einladungslink für ${i.email}`} onClick={() => inviteEmail(i.email, i.role)}>
              <RefreshCw size={16} />
            </IconButton>
            <RemoveButton label={`Einladung für ${i.email} zurückziehen`} onClick={() => run(() => api.del(`/api/boards/${boardId}/invitations`, { email: i.email }))} />
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-2">
        {team && <p className="text-sm text-[var(--ink-soft)]">Teammitglieder ohne Freigabe haben keinen Zugriff.</p>}
        <button
          type="button"
          aria-expanded={showAccess}
          aria-controls="tatsaechlicher-zugriff"
          onClick={() => setShowAccess((v) => !v)}
          className="inline-flex min-h-11 items-center gap-1.5 self-start rounded-lg px-1 text-sm font-medium text-accent-fg"
        >
          <ChevronDown size={16} className={cx('transition-transform', showAccess && 'rotate-180')} aria-hidden />
          Tatsächlichen Zugriff anzeigen
        </button>
        {showAccess && <AccessList boardId={boardId} />}
      </div>

      <TransferSection boardId={boardId} data={data} meId={me?.id} snapshot={snapshot} />
    </div>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <IconButton label={label} onClick={onClick} className="text-[var(--ink-soft)] hover:text-rose-600">
      <Trash2 size={16} />
    </IconButton>
  );
}

/** Suche über Teammitglieder und Gruppen; eine unbekannte E-Mail-Adresse wird eingeladen */
function AddForm({ data, onPick, onEmail }: { data: BoardSharing; onPick: (c: SharingCandidate, role: Role) => void; onEmail: (mail: string, role: Role) => void }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<SharingCandidate | null>(null);
  const [role, setRole] = useState<Role>('editor');
  const term = q.trim().toLowerCase();
  const matches = useMemo(
    () => (term && !picked ? data.candidates.filter((c) => `${c.name} ${c.detail}`.toLowerCase().includes(term)).slice(0, 6) : []),
    [term, picked, data.candidates],
  );
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.trim());
  const roles = picked?.kind === 'group' || (!picked && data.teamId) ? SHARED_ROLES : ROLES;
  const effectiveRole = (roles as readonly Role[]).includes(role) ? role : 'editor';
  const canSubmit = !!picked || isEmail;
  const reset = () => {
    setQ('');
    setPicked(null);
  };
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (picked) onPick(picked, effectiveRole);
        else if (isEmail) onEmail(q.trim().toLowerCase(), effectiveRole);
        else return;
        reset();
      }}
    >
      <label className="text-sm font-medium" htmlFor="share-search">
        Zu diesem Board einladen
      </label>
      <div className="flex flex-wrap gap-2">
        {picked ? (
          <span className="inline-flex min-h-11 min-w-0 flex-1 basis-full items-center gap-2 rounded-lg border border-accent bg-accent-soft/40 px-3 text-sm sm:basis-auto">
            {picked.kind === 'group' ? <Users size={15} aria-hidden /> : null}
            <span className="min-w-0 flex-1 truncate">
              {picked.kind === 'group' ? `Gruppe „${picked.name}“` : picked.name}
            </span>
            <button type="button" aria-label="Auswahl aufheben" className="inline-flex size-8 items-center justify-center rounded" onClick={reset}>
              <X size={15} />
            </button>
          </span>
        ) : (
          <input
            id="share-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={data.teamId ? 'Name, Gruppe oder E-Mail-Adresse' : 'E-Mail-Adresse'}
            autoComplete="off"
            aria-describedby="share-hint"
            className={cx(field, 'min-w-0 flex-1 basis-full sm:basis-auto')}
          />
        )}
        <select value={effectiveRole} onChange={(e) => setRole(e.target.value as Role)} aria-label="Rolle" className={cx(field, 'flex-1 sm:flex-none')}>
          {roles.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <Button type="submit" variant="primary" disabled={!canSubmit}>
          Freigeben
        </Button>
      </div>
      {matches.length > 0 && (
        <ul aria-label="Vorschläge" className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
          {matches.map((c) => (
            <li key={`${c.kind}-${c.id}`}>
              <button
                type="button"
                onClick={() => setPicked(c)}
                className="flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm hover:bg-black/[0.03] dark:hover:bg-white/5"
              >
                {c.kind === 'group' ? <span className={cx('size-2.5 rounded-full', GROUP_DOT[c.color ?? 'gray'])} aria-hidden /> : null}
                <span className="min-w-0 flex-1 truncate">
                  {c.kind === 'group' ? `Gruppe „${c.name}“` : c.name}
                  <span className="ml-1.5 text-xs text-[var(--ink-soft)]">{c.detail}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p id="share-hint" className="text-xs text-[var(--ink-soft)]">
        Gibt nur dieses Board frei.
        {data.teamId
          ? ' Gruppen erhalten höchstens „Bearbeiter“, Besitzer sind immer einzelne Teammitglieder. Personen außerhalb des Teams lädst du per E-Mail ein – sie bleiben Extern.'
          : ' Wer noch kein Konto hat, bekommt einen Einladungslink.'}
      </p>
    </form>
  );
}

function AccessList({ boardId }: { boardId: string }) {
  const { data } = useQuery({ queryKey: accessKey(boardId), queryFn: () => api.get<AccessEntry[]>(`/api/boards/${boardId}/access`) });
  if (!data) return <p className="text-sm text-[var(--ink-soft)]">Lade …</p>;
  return (
    <ul id="tatsaechlicher-zugriff" aria-label="Tatsächlicher Zugriff" className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
      {data.map((e) => (
        <li key={e.id} className="px-3 py-2">
          <details>
            <summary className="flex min-h-10 cursor-pointer list-none items-center gap-3 text-sm">
              <Avatar user={e} size={26} />
              <span className="min-w-0 flex-1 truncate">
                {e.name}
                {e.guest ? <span className={cx(BADGE.linkgast, 'ml-1.5')}>Linkgast</span> : e.external ? <span className={cx(BADGE.extern, 'ml-1.5')}>Extern</span> : null}
              </span>
              <span className="text-xs font-medium">{ROLE_LABELS[e.role]}</span>
              <span className="sr-only">– Quellen anzeigen</span>
            </summary>
            <ul className="mt-1 mb-1 ml-9 list-disc pl-4 text-xs text-[var(--ink-soft)]">
              {e.sources.map((s, i) => (
                <li key={i}>{sourceText(s)}</li>
              ))}
              {e.sources.length > 1 && <li className="list-none">Es gilt die höchste Rolle. Wer eine Quelle entfernt, lässt die anderen bestehen.</li>}
            </ul>
          </details>
        </li>
      ))}
    </ul>
  );
}

/** Besitz übergeben (L03): eine andere Person wird Besitzer, die eigene Rolle sinkt */
function TransferSection({ boardId, data, meId, snapshot }: { boardId: string; data: BoardSharing; meId: string | undefined; snapshot: BoardSnapshot }) {
  const [openForm, setOpenForm] = useState(false);
  const [target, setTarget] = useState('');
  const [myRole, setMyRole] = useState<'editor' | 'commenter' | 'viewer' | 'none'>('editor');
  const action = useBoardAction(boardId);
  // Teamboards: alle Teammitglieder; ohne Team: Personen mit Konto und Zugriff
  const options = data.teamId
    ? [
        ...data.people.filter((p) => !p.external && !p.guest && p.id !== meId).map((p) => ({ id: p.id, name: p.name })),
        ...data.candidates.filter((c) => c.kind === 'member' && c.id !== meId).map((c) => ({ id: c.id, name: c.name })),
      ]
    : snapshot.members.filter((m) => !m.guest && m.id !== meId).map((m) => ({ id: m.id, name: m.name }));
  const unique = [...new Map(options.map((o) => [o.id, o])).values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  return (
    <section aria-labelledby="transfer-title" className="rounded-xl border border-[var(--line)] p-3">
      <button
        type="button"
        id="transfer-title"
        aria-expanded={openForm}
        onClick={() => setOpenForm((v) => !v)}
        className="flex min-h-11 w-full items-center gap-1.5 text-left text-sm font-medium"
      >
        <ChevronDown size={16} className={cx('transition-transform', openForm && 'rotate-180')} aria-hidden />
        Besitz übergeben
      </button>
      {openForm && (
        <form
          className="mt-2 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const who = unique.find((u) => u.id === target);
            if (!who) return;
            const after = myRole === 'none' ? 'keinen Zugriff mehr' : `nur noch ${ROLE_LABELS[myRole]}`;
            if (!window.confirm(`${who.name} wird Besitzer von „${snapshot.board.title}“. Du hast danach ${after}. Fortfahren?`)) return;
            action.mutate(async () => {
              await api.post(`/api/boards/${boardId}/transfer`, { userId: target, myRole });
              toast.info(`${who.name} ist jetzt Besitzer.`);
            });
          }}
        >
          <p className="text-xs text-[var(--ink-soft)]">
            Nötig, bevor du ein Team verlässt, in dem du Boards allein besitzt.{data.teamId ? ' Besitzer können nur Teammitglieder werden.' : ''}
          </p>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Neuer Besitzer
            <select className={field} value={target} onChange={(e) => setTarget(e.target.value)} required>
              <option value="">Bitte wählen …</option>
              {unique.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Deine Rolle danach
            <select className={field} value={myRole} onChange={(e) => setMyRole(e.target.value as typeof myRole)}>
              <option value="editor">{ROLE_LABELS.editor}</option>
              <option value="commenter">{ROLE_LABELS.commenter}</option>
              <option value="viewer">{ROLE_LABELS.viewer}</option>
              <option value="none">Keine (Board verlassen)</option>
            </select>
          </label>
          <Button type="submit" variant="primary" className="self-start" disabled={!target}>
            Besitz übergeben
          </Button>
        </form>
      )}
    </section>
  );
}

/* ---------- Ohne Besitzrechte: wer hat Zugriff ---------- */

function ReadOnlyMembers({ snapshot }: { snapshot: BoardSnapshot }) {
  const boardId = snapshot.board.id;
  const action = useBoardAction(boardId);
  const { data: me } = useMe();
  return (
    <div className="flex flex-col gap-3 p-4">
      <p className="text-sm text-[var(--ink-soft)]">Freigaben verwalten die Besitzer dieses Boards.</p>
      <ul aria-label="Personen mit Zugriff" className="flex flex-col divide-y divide-[var(--line)]">
        {snapshot.members.map((m) => (
          <li key={m.id} className="flex min-h-14 items-center gap-3">
            <Avatar user={m} size={34} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                <PersonName id={m.id} name={m.name} />
                {m.id === me?.id && ' (du)'}
              </p>
              <Headline text={m.headline} />
              <p className="truncate text-xs text-[var(--ink-soft)]">
                {m.guest ? <span className={BADGE.linkgast}>Linkgast</span> : m.external ? <span className={BADGE.extern}>Extern</span> : null}
                {m.email}
              </p>
            </div>
            <span className="text-xs text-[var(--ink-soft)]">{ROLE_LABELS[m.role]}</span>
            {m.id === me?.id && (
              <RemoveButton
                label="Board verlassen"
                onClick={() => {
                  if (!window.confirm('Board wirklich verlassen?')) return;
                  action.mutate(async () => {
                    const res = await api.del<{ remaining?: AccessSource[] }>(`/api/boards/${boardId}/members/${m.id}`);
                    const via = res?.remaining?.map(sourceText);
                    if (via?.length) toast.info(`Deine persönliche Freigabe ist entfernt. Du hast weiterhin Zugriff: ${via.join(', ')}.`);
                  });
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
